import { IonButton, IonCol, IonGrid, IonIcon, IonItem, IonLabel, IonList, IonListHeader, IonRow, IonTextarea } from "@ionic/react";
import {
  albumsOutline,
  arrowDown,
  arrowUp,
  bookmarkOutline,
  chatbubbleOutline,
  eyeOffOutline,
} from "ionicons/icons";
import { GetPersonDetailsResponse } from "./userTypes";
import { ComponentProps, useEffect, useRef, useState } from "react";

import { customClientSelector, userHandleSelector } from "#/features/auth/authSelectors";
import { FetchFn } from "#/features/feed/Feed";
import PostCommentFeed, {
  PostCommentItem,
} from "#/features/feed/PostCommentFeed";
import {
  getModColor,
  getModIcon,
  getModName,
} from "#/features/moderation/useCanModerate";
import useModZoneActions from "#/features/moderation/useModZoneActions";
import { MaxWidthContainer } from "#/features/shared/AppContent";
import { getHandle, getRemoteHandle, isPost } from "#/helpers/lemmy";
import { useBuildGeneralBrowseLink } from "#/helpers/routes";
import useClient from "#/helpers/useClient";
import { LIMIT } from "#/services/lemmy";
import { useAppSelector } from "#/store";

import Scores from "./Scores";
import SocialMedia from "./SocialMedia";
import ProfileAvatar from "./ProfileAvatar";
import { UploadImageResponse } from "lemmy-js-client";
import styles from './Profile.module.css';
import BannerUpload from "./BannerUpload";

interface ProfileProps
  extends Pick<ComponentProps<typeof PostCommentFeed>, "onPull"> {
  person: GetPersonDetailsResponse;
}

export default function Profile({ person, onPull }: ProfileProps) {
  const customClient = useAppSelector(customClientSelector);
  const buildGeneralBrowseLink = useBuildGeneralBrowseLink();
  const client = useClient();
  const myHandle = useAppSelector(userHandleSelector);
  const { present: presentModZoneActions, role } = useModZoneActions({
    type: "ModeratorView",
  });

  const [profileImgData, setProfileImgData] = useState<UploadImageResponse | null>(null);
  const [bannerImgData, setBannerImgData] = useState<UploadImageResponse | null>(null);
  const bannerImgInputRef = useRef<HTMLInputElement | null>(null);
  const [bio, setBio] = useState<string>("");

  const isSelf = getRemoteHandle(person.person_view.person) === myHandle;
  const personInfo = person?.person_view?.person;

  useEffect(() => {
    if (personInfo.bio) {
      setBio(personInfo.bio);
    }
    if (personInfo.avatar) {
      setProfileImgData({
        msg: 'ok',
        url: personInfo.avatar
      });
    }
    if (personInfo.banner) {
      setBannerImgData({
        msg: 'ok',
        url: personInfo.banner
      });
    }
  }, [person]);
  

  const fetchFn: FetchFn<PostCommentItem> = async (pageData, ...rest) => {
    const response = await client.getPersonDetails(
      {
        ...pageData,
        limit: LIMIT,
        username: getHandle(person.person_view.person),
        sort: "New",
      },
      ...rest,
    );
    return [...response.posts, ...response.comments].sort(
      (a, b) =>
        getPostCommentItemCreatedDate(b) - getPostCommentItemCreatedDate(a),
    );
  };

  const openImgFileDialog = () => {
    bannerImgInputRef.current?.click();
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    const uploadImage = {
      image: file,
      name: file.name,
      size: file.size,
      type: file.type,
    };
    const response = await client.uploadImage(uploadImage);
    return response || null;
  }

  const handleProfileImgChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const response = await handleImageUpload(event);
    if (response) {
      setProfileImgData(response);
    }
  }

  const handleBannerImgChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('?')
    const response = await handleImageUpload(event);
    console.log('?', response)
    if (response) {
      setBannerImgData(response);
    }
    // setBannerImg(imgFile);
  }

  const handleUpdateProfile = async () => {
    try {
      const response = await customClient.saveUserSettings({
        avatar: profileImgData?.url,
        banner: bannerImgData?.url,
        bio,
      });
      console.log('response >>>', response);
    } catch (error) {
      console.error('error', error)
    }
  }

  const header = (
    <MaxWidthContainer>
      <IonGrid>
        <IonRow className="ion-align-items-center">
          <IonCol size="auto">
            <IonItem lines="none">
              <ProfileAvatar
                profileImgUrl={profileImgData?.url || ''}
                handleProfileImgChange={handleProfileImgChange}
                isSelf={isSelf}
              />
            </IonItem>
          </IonCol>
          <IonCol>
            <IonListHeader className={`${styles.username}${personInfo.name?.length > 14 ? ' ' + styles.usernameLong : ''} ion-text-center ion-padding-vertical`}>
              {personInfo.name}
            </IonListHeader>
            <Scores
              aggregates={person.person_view.counts}
              accountCreated={person.person_view.person.published}
            />
            {/* <IonItem lines="none">
              <BannerUpload
                bannerImgUrl={bannerImgData?.url}
                handleImgChange={handleBannerImgChange}
              />
            </IonItem> */}
          </IonCol>
        </IonRow>
        {isSelf || personInfo.bio
          ? (
            <IonRow className="ion-justify-content-center">
              <IonItem className={styles.bioContainer} lines="none">
                <IonTextarea
                  value={bio}
                  onIonInput={(e) => setBio(e.detail.value!)}
                  placeholder="About me"
                  autoGrow
                  maxlength={400}
                  className={styles.textarea}
                  contentEditable={isSelf}
                />
              </IonItem>
            </IonRow>
          )
          : null
        }
        <IonRow className="ion-justify-content-end">
          {isSelf
            ? (
              <IonItem lines="none">
                <IonButton onClick={handleUpdateProfile}>
                  Save Changes
                </IonButton>
              </IonItem>
            )
            : null
          }
        </IonRow>
      </IonGrid>
      <SocialMedia
        person={person.person_view}
      />
      <IonList inset>
        {/* <IonItem
          routerLink={buildGeneralBrowseLink(
            `/u/${getHandle(person.person_view.person)}/posts`,
          )}
        >
          <IonIcon icon={albumsOutline} color="primary" slot="start" />{" "}
          <IonLabel className="ion-text-nowrap">Posts</IonLabel>
        </IonItem> */}
        <IonItem
          routerLink={buildGeneralBrowseLink(
            `/u/${getHandle(person.person_view.person)}/comments`,
          )}
        >
          <IonIcon icon={chatbubbleOutline} color="primary" slot="start" />{" "}
          <IonLabel className="ion-text-nowrap">Comments</IonLabel>
        </IonItem>
        {isSelf && (
          <>
            {/* <IonItem
              routerLink={buildGeneralBrowseLink(
                `/u/${getHandle(person.person_view.person)}/saved`,
              )}
            >
              <IonIcon icon={bookmarkOutline} color="primary" slot="start" />{" "}
              <IonLabel className="ion-text-nowrap">Saved</IonLabel>
            </IonItem> */}
            <IonItem
              routerLink={buildGeneralBrowseLink(
                `/u/${getHandle(person.person_view.person)}/upvoted`,
              )}
            >
              <IonIcon icon={arrowUp} color="primary" slot="start" />{" "}
              <IonLabel className="ion-text-nowrap">Upvoted</IonLabel>
            </IonItem>
            <IonItem
              routerLink={buildGeneralBrowseLink(
                `/u/${getHandle(person.person_view.person)}/downvoted`,
              )}
            >
              <IonIcon icon={arrowDown} color="primary" slot="start" />{" "}
              <IonLabel className="ion-text-nowrap">Downvoted</IonLabel>
            </IonItem>
            {/* <IonItem
              routerLink={buildGeneralBrowseLink(
                `/u/${getHandle(person.person_view.person)}/hidden`,
              )}
            >
              <IonIcon icon={eyeOffOutline} color="primary" slot="start" />{" "}
              <IonLabel className="ion-text-nowrap">Hidden</IonLabel>
            </IonItem> */}
          </>
        )}
      </IonList>
      {isSelf && role && (
        <IonList inset>
          <IonItem detail onClick={presentModZoneActions}>
            <IonIcon
              icon={getModIcon(role)}
              color={getModColor(role)}
              slot="start"
            />{" "}
            <IonLabel className="ion-text-nowrap">
              {getModName(role)} Zone
            </IonLabel>
          </IonItem>
        </IonList>
      )}
    </MaxWidthContainer>
  );

  return (
    <PostCommentFeed
      fetchFn={fetchFn}
      header={header}
      filterHiddenPosts={false}
      filterKeywordsAndWebsites={false}
      onPull={onPull}
    />
  );
}

export function getPostCommentItemCreatedDate(item: PostCommentItem): number {
  if (isPost(item)) return Date.parse(item.post.published);
  return Date.parse(item.comment.published);
}
